import {
  useRef,
  useEffect,
  useState,
  useCallback,
  RefObject,
  lazy,
  Suspense,
} from 'react';
import { useInView } from 'react-intersection-observer';
import MuxPlayerLazy from '@mux/mux-player-react/lazy';
import type { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import styled from 'styled-components';

// This lazy imports the non lazy mux player so the page doesn't need to wait
// for the mux import before loading.
const MuxPlayerNotLazy = lazy(() => import('@mux/mux-player-react'));

export const getMuxPosterUrl = (
  playbackId: string | undefined,
  time: number,
  width?: number,
) =>
  `https://image.mux.com/${playbackId}/thumbnail.webp?${width ? `width=${width}&` : ''}time=${time}`;

const MuxWrapper = styled.div`
  mux-player,
  mux-video {
    --media-background-color: transparent !important;
  }
`;

// Used to lazy load the MuxPlayer, and pause it when not in view,
// and play when it comes back into view, IF the user hasn't manually paused it.
// If the video is manually controlled, it will pause, but not resume playback based on visibility.

type MuxPlayerAutoPauseProps = React.ComponentProps<typeof MuxPlayerLazy> & {
  wrapperPosition?: React.CSSProperties['position'];
  isManuallyControlled?: boolean;
  /** External pause override (e.g. a shared chapter play/pause control). Always wins over the in-view autoplay state. */
  paused?: boolean;
  videoRefs?: RefObject<any[]>;
  index?: number;
  posterTime?: number;
  /** Poster width in device pixels. The player gets the same URL, so the poster downloads once. */
  posterWidth?: number;
  /** Renders the poster (and the non-lazy player) only once the wrapper is within a viewport of the screen. */
  deferUntilNearView?: boolean;
  height?: string;
  width?: string;
  lazy?: boolean;
};

const MuxPlayerAutoPause = ({
  wrapperPosition,
  isManuallyControlled = false,
  paused,
  videoRefs,
  index,
  posterTime = 0,
  posterWidth,
  deferUntilNearView = false,
  height,
  width,
  lazy = true,
  ...props
}: MuxPlayerAutoPauseProps) => {
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  // Manual players start paused, so a late mount never calls play() before the in-view effect runs.
  const [playerPaused, setPlayerPaused] = useState(isManuallyControlled);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const { ref: nearViewRef, inView: isNearView } = useInView({
    rootMargin: '100%',
    triggerOnce: true,
    skip: !deferUntilNearView,
  });
  const canLoad = !deferUntilNearView || isNearView;
  const wrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      inViewRef(node);
      nearViewRef(node);
    },
    [inViewRef, nearViewRef],
  );
  const userPausedRef = useRef(false);
  const elementRef = useRef<MuxPlayerRefAttributes>(null);

  const handlePause = (
    event?: CustomEvent<{ composed: true; detail: any }>,
  ) => {
    if (inView && document.visibilityState === 'visible' && !paused) {
      userPausedRef.current = true;
      setPlayerPaused(true);
    }
    if (props.onPause && event) {
      props.onPause(event);
    }
  };
  const handlePlay = () => {
    userPausedRef.current = false;
    setPlayerPaused(false);
  };

  const setUpRefs = () => {
    if (elementRef.current !== null && index !== undefined) {
      const videoEl = elementRef?.current?.shadowRoot
        ?.querySelector('mux-video')
        ?.shadowRoot?.querySelector('video');
      if (videoEl && videoRefs && !videoRefs.current[index]) {
        videoRefs.current[index] = videoEl;
      }
    }
  };

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        setPlayerPaused(true);
      } else {
        if (!userPausedRef.current && !isManuallyControlled && !paused) {
          setPlayerPaused(false);
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isManuallyControlled, paused]);

  useEffect(() => {
    if (!inView) {
      setPlayerPaused(true);
    } else {
      if (!userPausedRef.current && !isManuallyControlled && !paused) {
        setPlayerPaused(false);
      }
    }
  }, [inView, isManuallyControlled, paused]);

  const posterSrc =
    props.poster || getMuxPosterUrl(props.playbackId, posterTime, posterWidth);

  const { loading: _loading, ...notLazyProps } = props;

  const handleLoadedData = () => {
    if (!lazy) {
      setUpRefs();
    }
    // sometimes onLoadedData doesn't get called in safari ios
    isManuallyControlled && setHasLoaded(true);
  };

  const targetPaused = playerPaused || Boolean(paused);

  const commonPlayerProps = {
    ...(posterWidth ? { poster: posterSrc } : {}),
    ref: elementRef,
    paused: targetPaused,
    onPause: handlePause,
    onPlay: handlePlay,
    // mux-player-react skips applying a controlled `paused` prop while `autoplay` is set
    // and the element hasn't played yet, so autoplay can win the race on mount — reassert here.
    onPlaying: () => {
      setHasLoaded(true);
      if (targetPaused) {
        elementRef.current?.pause();
      }
    },
    onLoadedData: handleLoadedData,
  };

  return (
    <MuxWrapper
      ref={wrapperRef}
      style={{
        ...(height && { height }),
        ...(width && { width }),
        ...(wrapperPosition && { position: wrapperPosition }),
      }}
    >
      {lazy ? (
        <MuxPlayerLazy
          {...props}
          {...commonPlayerProps}
          onCanPlay={() => {
            setUpRefs();
          }}
        />
      ) : canLoad ? (
        <Suspense fallback={null}>
          <MuxPlayerNotLazy
            {...(notLazyProps as React.ComponentProps<typeof MuxPlayerNotLazy>)}
            {...commonPlayerProps}
            onCanPlay={() => {
              setUpRefs();
            }}
            style={{
              ...props.style,
              ...(height && { height: height }),
            }}
          />
        </Suspense>
      ) : null}
      {canLoad && !hasLoaded && (
        <img
          src={posterSrc}
          alt='Loading thumbnail for video'
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </MuxWrapper>
  );
};

export default MuxPlayerAutoPause;
